import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useAuth } from "../hooks/authContext";
import { useUser } from "../hooks/userContext";
import { format, isValid, parseISO } from "date-fns";
//import { UserRole } from '../roleConfig';
import { getCurrentUAETime, formatUAETime, logTimezoneInfo, uaeToLocal } from "../../utils/dateUtils";
import { fetchTableDataSU } from "../api/fetchTableDataSU";
import { fetchS3DataSU } from "../api/fetchS3DataSU";
import { fetchWithRetry, buildUiFiles, type UiFile } from "../api/fetchTemplateDetails";
import { filterTableDataByRole } from "../hooks/userRoleFilterData";
import { S3_CONFIG } from '../config/apiConfig';
import { getEnvironment } from "../filePathUtils";

interface DataContextType {
  tableData: any[];
  refreshData: (refreshApiOnly?: boolean) => Promise<void>;
  isLoading: boolean;
  dropdownOptions: {
    entityCodes: string[];
    entityNames: string[];
    templateNames: string[];
    submissionTypes: string[];
    submissionVersions: string[];
    submissionPeriods: string[];
    status: string[];
    year: string[];
  };
  stats: {
    totalFiles: number;
    uploadedFiles: number;
    dueDate: string;
    pendingUploads: number;
    failedUploads: number;
    warningUploads: number;
    reuploadRequired: number;
    successfulUploads: number;
    inProgressUploads: number;
  };
  setStats: (stats: any) => void;
  addTableData: (newData: any) => void;
  getSubmissionHistory: (params: {
    entityCode: string;
    templateName: string;
    submissionPeriod: string;
    submissionVersion: string;
  }) => any[];
  calculateStats: (data: any[]) => void;

  templateFiles: UiFile[];
  templatesLoading: boolean;
  templatesError: string | null;
  refreshTemplates: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

const calculatePortalStatus = (closeTime: string, openTime: string) => {
  if (!closeTime) {
    return {
      status: 'No close time set',
      colorScheme: { bg: 'rgba(128, 128, 128, 0.15)', text: '#666666' }
    };
  }

  if (!openTime) {
      return {
        status: 'No open time set',
        colorScheme: { bg: 'rgba(128, 128, 128, 0.15)', text: '#666666' }
     };
  }

  const now = getCurrentUAETime();
  const openDate = new Date(openTime);
  // Parse the UAE time string directly (API sends UAE time)
  const closeDate = new Date(closeTime);
  const timeDiffInDays = Math.ceil((now.getTime() - closeDate.getTime()) / (1000 * 60 * 60 * 24));
  const daysUntilClose = Math.ceil((closeDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));


    if (openDate > now) {
        const formattedOpenTime = formatUAETime(openDate, "do MMM yyyy");
        return {
          status: `Upcoming – Opens on ${formattedOpenTime}`,
          colorScheme: { bg: 'rgba(128, 128, 128, 0.15)', text: '#666666' }
        };
    }


  if (closeDate < now) {
    // Portal is closed, check if it was closed recently
    if (timeDiffInDays <= 15) {
      return {
        status: 'Closed Recently',
        colorScheme: { bg: 'rgba(255, 165, 0, 0.15)', text: '#CC7000' }
      };
    } else {
      return {
        status: 'Closed',
        colorScheme: { bg: 'rgba(220, 0, 0, 0.15)', text: '#B30000' }
      };
    }
  } else {
    const formattedCloseTime = formatUAETime(closeTime, "do MMM yyyy, h:mm aa");
    return {
      status: `Open until ${formattedCloseTime}`,
      colorScheme: daysUntilClose <= 1
        ? { bg: 'rgba(255, 165, 0, 0.15)', text: '#CC7000' }
        : daysUntilClose <= 7
          ? { bg: 'rgba(255, 190, 0, 0.15)', text: '#B37800' }
          : { bg: 'rgba(0, 180, 0, 0.15)', text: '#006B00' }
    };
  }
};

/*interface TemplateAccess {
  code: string;
  files: string[];
}

/*export const parseUploadAccesses = (uploadAccesses: string | string[]): TemplateAccess[] => {
  const accessString = Array.isArray(uploadAccesses) ? uploadAccesses.join(',') : uploadAccesses;

  if (!accessString) {
    console.log("No upload accesses provided");
    return [];
  }

  // Split by entity codes
  const entitySections = accessString.split('||');
  console.log("Entity sections:", entitySections);

  const parsed = entitySections.map(access => {
    const [codeSection, filesSection] = access.split('|files:');

    // Fix: Extract only the entity code without the role part
    let code = codeSection.replace('code:', '');

    // If code has format like "0050:Primary", extract only "0050"
    if (code.includes(':')) {
      code = code.split(':')[0];
    }

    // Clean and parse files
    const files = (filesSection || '')
      .replace('[', '')
      .replace(']', '')
      .split(';')
      .map(f => f.trim())
      .filter(file => file.length > 0);

    return {
      code,
      files
    };
  });

  console.log("Final parsed upload accesses:", parsed);
  return parsed;
};*/

export const DataProvider: React.FC<React.PropsWithChildren<{}>> = observer(({ children }) => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [s3Data, setS3Data] = useState<any[]>([]); // New state for S3 data
  const [isLoading, setIsLoading] = useState(true);
  const [dropdownOptions, setDropdownOptions] = useState<{
    entityNames: string[];
    entityCodes: string[];
    templateNames: string[];
    submissionTypes: string[];
    submissionVersions: string[];
    submissionPeriods: string[];
    year: string[];
    status: string[];
  }>({
    entityNames: [],
    entityCodes: [],
    templateNames: [],
    submissionTypes: [],
    submissionVersions: [],
    submissionPeriods: [],
    year: [],
    status: ['Accepted'],
  });
  const [stats, setStats] = useState({
    totalFiles: 0,
    uploadedFiles: 0,
    dueDate: "",
    pendingUploads: 0,
    failedUploads: 0,
    warningUploads: 0,
    reuploadRequired: 0,
    successfulUploads: 0,
    inProgressUploads: 0,
  });

  const [templateFiles, setTemplateFiles] = useState<UiFile[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  const [rawCombinedData, setRawCombinedData] = useState<any[]>([]); // store raw for recompute

  const { idToken } = useAuth();
  const user = useUser();

  const loadTemplateFiles = useCallback(async (): Promise<UiFile[]> => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
        const S3_KEY_PREFIX = getEnvironment() === 'production'? S3_CONFIG.KEY_File : S3_CONFIG.KEY_File_Dev;
        const API_URL_TEMPLATE_LIST =
          `https://8bjw23y28h.execute-api.eu-west-1.amazonaws.com/prod/s3/list?includeVersions=true&prefix=${encodeURIComponent(S3_KEY_PREFIX)}`;
      const api = await fetchWithRetry(API_URL_TEMPLATE_LIST,3,1000);
      const ui = buildUiFiles(api?.objects ?? []);
      const safeUi = Array.isArray(ui) ? ui : [];
      setTemplateFiles(safeUi);
      return safeUi; // return loaded files
    } catch (e) {
      const msg = e instanceof Error ? e.message : typeof e === "string" ? e : "Unknown error";
      setTemplatesError(msg);
      setTemplateFiles([]); // ensure it's never undefined
      return [];
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  const refreshTemplates = useCallback(async (): Promise<void> => {
    await loadTemplateFiles();
  }, [loadTemplateFiles]);

  // Process data for UI display
  const processData = (data: any[], templates: UiFile[]) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    /* --------------------------
       De-dup by submission_id
       keep the latest by log_timestamp (fallback: upload_time_stamp)
    --------------------------- */

    // Normalize 'YYYY-MM-DD HH:mm:ss.SSS...' to 9 fractional digits for safe string compare
    function normalizeTimestamp(ts?: string): string {
      if (!ts) return '';
      const s = String(ts).trim();
      const [datePart, timePart] = s.split(' ');
      if (!datePart || !timePart) return s;
      const [hms, fracRaw = ''] = timePart.split('.');
      const frac = (fracRaw ?? '').padEnd(9, '0').slice(0, 9);
      return `${datePart} ${hms}.${frac}`;
    }

    // Build a comparable key: prefer log_timestamp, else upload_time_stamp
    function compareKey(row: any): string {
      return normalizeTimestamp(row?.log_timestamp || row?.upload_time_stamp || '');
    }

    // Reduce to one latest record per submission_id
    const latestPerSubmissionMap = data.reduce((acc, item) => {
      const key = String(item?.submission_id ?? '').trim();
      if (!key) return acc; // skip missing submission_id

      const prev = acc.get(key);
      if (!prev) {
        acc.set(key, item);
      } else {
        // keep the item with the larger normalized timestamp
        if (compareKey(item) > compareKey(prev)) {
          acc.set(key, item);
        }
      }
      return acc;
    }, new Map<string, any>());

    // assign back to data
    data = Array.from(latestPerSubmissionMap.values());

    // Extract all unique entity codes from the data
    const entityCodes = [...new Set(data.map(item => item.entity_code))];

    // Find primary/secondary users per entity
    const primarySecondaryUsers = entityCodes.reduce((acc, entityCode) => {
      const entityItems = data.filter(item => item.entity_code === entityCode);
      let primaryEmail = '';
      let secondaryEmail = '';

      for (const item of entityItems) {
        if (item.primary_user_email) {
          primaryEmail = item.primary_user_email.toLowerCase();
        }
        if (item.secondary_user_email) {
          secondaryEmail = item.secondary_user_email.toLowerCase();
        }
        if (primaryEmail && secondaryEmail) break;
      }

      acc[entityCode] = { primaryEmail, secondaryEmail };
      return acc;
    }, {} as Record<string, { primaryEmail: string; secondaryEmail: string }>);

    let filteredData;

    console.log("Processing data with user:", {
      userExists: !!user,
      userRoles: user?.roles,
      userEmail: user?.email ? user.email.toLowerCase() : undefined,
      uploadAccesses: user?.upload_accesses
    });

    /*if (user?.roles.includes(UserRole.SBU)) {
      // Parse user's upload access permissions
      const uploadAccesses = parseUploadAccesses(user.upload_accesses || '');
      console.log("User upload accesses:", uploadAccesses);
      console.log("User email:", user.email ? user.email.toLowerCase() : undefined);

      // Filter data for SBU users based on their access permissions
      filteredData = data.filter(item => {
        // Normalize entity code by removing leading zeros and whitespace
        const normalizedItemCode = item.entity_code.toString().replace(/^0+/, '').trim();

        const accessEntry = uploadAccesses.find(access => {
          const normalizedAccessCode = access.code.toString().replace(/^0+/, '').trim();
          return normalizedItemCode === normalizedAccessCode;
        });

        if (!accessEntry) {
          return false;
        }

        // Check if user has access to this template
        const hasTemplateAccess = accessEntry.files.some(file => file === item.template_name);
        return hasTemplateAccess;
      });
    } else {
      // For SuperUser and Admin, use all data
      filteredData = data;
    }*/

    filteredData = filterTableDataByRole(data, user?.roles?? [], user);


    const groupedData: Record<string, any[]> = filteredData.reduce(
      (acc, item) => {
        const key = `${item.submission_id}-${item.period}-${item.year}`;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      },
      {} as Record<string, any[]>
    );

    const formatDate = (dateString: string): string => {
      if (!dateString) return '';

      // Parse the UAE time string
      const uaeDate = new Date(dateString);

      // Calculate the time difference between UAE and local timezone
      const uaeOffset = 240; // UAE is UTC+4 (240 minutes)
      const localOffset = new Date().getTimezoneOffset();
      const diffInMinutes = uaeOffset + localOffset;

      // Convert UAE time to local time
      const localDate = new Date(uaeDate.getTime() - (diffInMinutes * 60000));

      // Format in local time
      return format(localDate, "do MMM yyyy h:mm aa");
    };

    const getSubmissionName = (fileName: string): string => {
      const map: Record<string, string> = {
        "Trial Balance Plus - Actual": "1A - 1B - Actual",
        "Trial Balance Plus - Budget 12M": "1A - 1B - Budget",
        "Business Data - Actual": "2A - Actual",
        "Business Data - Budget": "2A - Budget"
      };
      return map[fileName] || "Unknown Submission";
    };

    const getLatestTemplate = (templateName: string) => {
      if (!templateName || !templates?.length) return null;

      // Normalize input name (remove extension, lowercase)
      const baseName = templateName.replace(/\.xlsx$/i, "").trim().toLowerCase();

      // Find template by comparing without extension
      const match = templates.find(tf =>
        tf.name.replace(/\.xlsx$/i, "").trim().toLowerCase() === baseName
      );

      return match?.latestVersionOrdinal ?? null;
    };

    return Object.entries(groupedData).map(([, items]) => {
      // First find the highest file version
      const maxFileVersion = Math.max(...items.map(item =>
        parseFloat(item.file_version) || 0
      ));

      // Filter items with the highest file version
      const highestVersionItems = items.filter(item =>
        parseFloat(item.file_version) === maxFileVersion
      );

      // Then get the latest timestamp among those items
      const latestItem = highestVersionItems.reduce((latest, item) =>
        new Date(item.log_timestamp) > new Date(latest.log_timestamp)
          ? item
          : latest
        , highestVersionItems[0]);

      // Find the most recent Upload item's timestamp from highest version items
      const uploadItems = highestVersionItems.filter(item =>
        item.sub_status === "In-progress" ||
        item.sub_status === "Accepted" ||
        item.sub_status === "Warning" ||
        item.sub_status === "CF In-progress" ||
        item.sub_status === "CF Warning" ||
        item.sub_status === "CF Accepted" ||
        item.sub_status === "CF Failed" ||
        item.sub_status === "Cluster Failed" ||
        item.sub_status === "Failed" ||
        item.sub_status === "DLR failed"
      );

      let lastUploadedTimestamp = '';
      let lastUploadedUserId = '';  // Track the user_id who performed the last upload

      if (uploadItems.length > 0) {
        // Find the item with the most recent timestamp
        const mostRecentItem = uploadItems.reduce((latest, item) => {
          const itemDate = new Date(item.log_timestamp);
          return !latest || itemDate > new Date(latest.log_timestamp) ? item : latest;
        }, uploadItems[0]);

        lastUploadedTimestamp = mostRecentItem.log_timestamp;
        lastUploadedUserId = mostRecentItem.user_id || '';  // Get the user_id from the same item
      }

      const portalInfo = calculatePortalStatus(latestItem.upload_close_time,latestItem.upload_open_time);

      // Primary/secondary user info for this entity
      const userInfo = primarySecondaryUsers[latestItem.entity_code] || {
        primaryEmail: '',
        secondaryEmail: ''
      };

      return {
        entityCode: String(latestItem.entity_code).padStart(4, '0'),
        entityName: latestItem.entity_name,
        entityCodeAbbr: `${String(latestItem.entity_code).padStart(4, '0')}-${latestItem.entity_name}`,
        fileName: latestItem.template_name,
        submissionVersions: latestItem.sub_version,
        fileVersion: latestItem.sub_status === 'Upload pending' ? 0 : (parseInt(latestItem.file_version) || 0),

        submissionType: getSubmissionName(latestItem.template_name),
        versionInfo: getLatestTemplate(latestItem.template_name),

        status: latestItem.sub_status,

        // Convert all timestamps to local time
        lastUpdatedDate: latestItem.log_timestamp ? formatDate(latestItem.log_timestamp) : '',
        lastUpdatedDateISO: latestItem.log_timestamp ?
          uaeToLocal(latestItem.log_timestamp).toISOString() : '',

        latestUploadedDate: latestItem.upload_time_stamp ? formatDate(latestItem.upload_time_stamp) : lastUploadedTimestamp
          ? formatDate(lastUploadedTimestamp) : '',
        latestUploadedDateISO: latestItem.upload_time_stamp ? uaeToLocal(latestItem.upload_time_stamp) : lastUploadedTimestamp
          ? uaeToLocal(lastUploadedTimestamp) : '',

        lastUploaded: lastUploadedTimestamp ? formatDate(lastUploadedTimestamp) : '',
        lastUploadedISO: lastUploadedTimestamp ?
          uaeToLocal(lastUploadedTimestamp).toISOString() : '',

        portalStatus: portalInfo.status,
        portalColorScheme: portalInfo.colorScheme,
        dueDate: latestItem.due_date ?
          uaeToLocal(latestItem.due_date).toISOString() : '',

        submissionPeriod: latestItem.period,
        portalCloseTime: latestItem.upload_close_time || '',
        upload_open_time: latestItem.upload_open_time || '',

        // Store original UAE timestamp for reference
        log_timestamp: latestItem.log_timestamp ?
          uaeToLocal(latestItem.log_timestamp).toISOString() : '',

        subMonth: latestItem.sub_month,
        // Use the user_id from the most recent upload item - this is the person who uploaded the file
        user_id: lastUploadedUserId || '',
        uploadedBy: lastUploadedUserId || '',  // Additional field for clarity
        // User information for comparison
        currentUserEmail: user?.email ? user.email.toLowerCase() : '',
        primaryUserEmail: userInfo.primaryEmail || '',
        secondaryUserEmail: userInfo.secondaryEmail || '',
        template_name: latestItem.template_name,
        s3_url: latestItem.s3_url,
        submissionId: `${latestItem.submission_id}-${latestItem.period === '-' ? 'BUD' : latestItem.period}-${latestItem.year}`,
        year: latestItem.year,
        year_period: `${latestItem.year} - ${latestItem.period === '-' ? 'BUD' : latestItem.period}`,
      };
    });
  };

  const generateDropdownOptions = (
    data: any[]
  ): {
    entityNames: string[];
    entityCodes: string[];
    templateNames: string[];
    submissionTypes: string[];
    submissionVersions: string[];
    submissionPeriods: string[];
    year: string[];
    status: string[];
  } => {
    const options = data.reduce(
      (acc, item) => {
        acc.entityNames.add(item.entityName);
        acc.entityCodes.add(item.entityCode);
        acc.templateNames.add(item.fileName);
        if (item.submissionType) acc.submissionTypes.add(item.submissionType); // fixed
        acc.submissionVersions.add(item.submissionVersions);
        acc.submissionPeriods.add(item.submissionPeriod);
        acc.year.add(item.year);
        acc.status.add(item.status);
        return acc;
      },
      {
        entityNames: new Set<string>(),
        entityCodes: new Set<string>(),
        templateNames: new Set<string>(),
        submissionTypes: new Set<string>(),
        submissionVersions: new Set<string>(),
        submissionPeriods: new Set<string>(),
        year: new Set<string>(),
        status: new Set<string>(),
      }
    );

    return {
      entityNames: Array.from(options.entityNames),
      entityCodes: Array.from(options.entityCodes),
      templateNames: Array.from(options.templateNames),
      submissionTypes: Array.from(options.submissionTypes),
      submissionVersions: Array.from(options.submissionVersions),
      submissionPeriods: Array.from(options.submissionPeriods),
      year: Array.from(options.year),
      status: Array.from(options.status),
    };
  };

  const calculateStats = useCallback((data: any[]): void => {
    const currentMonth = format(new Date(), 'yyyy-MM');
    const filteredData = data.filter(item => {
      const isCurrentMonth = item.subMonth === currentMonth;
      const isHistorical = item.comment?.toLowerCase().includes('historical');
      return isCurrentMonth || isHistorical;
    });

    const totalFiles = filteredData.length;
    const uploadedFiles = filteredData.filter((item) =>
      [
        "In-progress",
        "Warning",
        "Failed",
        "Accepted",
        "Re-Upload Requested",
      ].includes(item.status)
    ).length;

    const pendingUploads = filteredData.filter((item) =>
      ["Upload pending"].includes(item.status)
    ).length;

    const failedUploads = filteredData.filter((item) =>
      ["Failed"].includes(item.status)
    ).length;

    const warningUploads = filteredData.filter((item) =>
      ["Warning"].includes(item.status)
    ).length;

    const reuploadRequired = filteredData.filter(
      (item) => item.status === "Re-Upload Requested"
    ).length;

    const successfulUploads = filteredData.filter((item) =>
      ["Accepted"].includes(item.status)
    ).length;

    const inProgressUploads = filteredData.filter((item) =>
      ["In-progress"].includes(item.status)
    ).length;

    let earliestDueDate = "";
    if (filteredData.length > 0) {
      const validDates = filteredData
        .map(item => {
          if (!item.portalCloseTime) return null;
          try {
            const date = parseISO(item.portalCloseTime);
            return isValid(date) ? date.getTime() : null;
          } catch (error) {
            console.error("Error parsing date:", item.portalCloseTime, error);
            return null;
          }
        })
        .filter((date): date is number => date !== null);

      if (validDates.length > 0) {
        const earliestDate = new Date(Math.min(...validDates));
        try {
          earliestDueDate = format(earliestDate, "do MMM h:mm a");
        } catch (error) {
          console.error("Error formatting date:", earliestDate, error);
          earliestDueDate = "Invalid Date";
        }
      }
    }

    setStats(prevStats => {
      if (
        prevStats.totalFiles !== totalFiles ||
        prevStats.uploadedFiles !== uploadedFiles ||
        prevStats.dueDate !== earliestDueDate ||
        prevStats.pendingUploads !== pendingUploads ||
        prevStats.failedUploads !== failedUploads ||
        prevStats.warningUploads !== warningUploads ||
        prevStats.reuploadRequired !== reuploadRequired ||
        prevStats.successfulUploads !== successfulUploads ||
        prevStats.inProgressUploads !== inProgressUploads
      ) {
        return {
          totalFiles,
          uploadedFiles,
          dueDate: earliestDueDate,
          pendingUploads,
          failedUploads,
          warningUploads,
          reuploadRequired,
          successfulUploads,
          inProgressUploads,
        };
      }
      return prevStats;
    });
  }, []);

  const fetchData = async (refreshApiOnly: boolean = false): Promise<void> => {
    if (!idToken || !user?.email) return;

    setIsLoading(true);
    try {
      let newData = s3Data;

      if (!refreshApiOnly && s3Data.length === 0) {
        newData = (await fetchS3DataSU(idToken)) || [];
      }

      const latestTimestamp = newData.reduce((latest, item) => {
        const itemTimestamp = item.log_timestamp;
        return !latest || (itemTimestamp && itemTimestamp > latest) ? itemTimestamp : latest;
      }, '');

      const apiData = await fetchTableDataSU(idToken, user.email, latestTimestamp, true); // Set productionOnly to true
      console.log("API Data:", apiData);
      const newCombinedData = Array.isArray(apiData) ? [...apiData, ...newData] : [...newData];

      // Store raw for possible recompute if templates refresh later
      setRawCombinedData(newCombinedData);

      const processedData = processData(newCombinedData, templateFiles);
      const newDropdownOptions = generateDropdownOptions(processedData);

      // Batch all state updates together
      setS3Data(newData);
      setTableData(processedData);
      setDropdownOptions(newDropdownOptions);

      requestAnimationFrame(() => {
        calculateStats(processedData);
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load: ensure templates are ready, then fetch & process data
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!idToken || !user?.email) return;

      try {
        await loadTemplateFiles(); // ensure templates are ready
        await fetchData();
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    if (idToken && user?.email) {
      fetchInitialData();
    }
  }, [idToken, user?.email, loadTemplateFiles]);

  // If templates refresh later, recompute processed rows from raw
  useEffect(() => {
    if (rawCombinedData.length > 0) {
      const processed = processData(rawCombinedData, templateFiles);
      setTableData(processed);
      setDropdownOptions(generateDropdownOptions(processed));
      requestAnimationFrame(() => calculateStats(processed));
    }
  }, [templateFiles]); // re-derive on template changes

  const refreshData = async (refreshApiOnly: boolean = false) => {
    await fetchData(refreshApiOnly);
  };

  const addTableData = (newData: any) => {
    setTableData(prevData => {
      const updatedData = [...prevData, newData];
      requestAnimationFrame(() => {
        calculateStats(updatedData);
      });
      return updatedData;
    });
  };

  const getSubmissionHistory = ({
    entityCode,
    templateName,
    submissionPeriod,
    submissionVersion
  }: {
    entityCode: string;
    templateName: string;
    submissionPeriod: string;
    submissionVersion: string;
  }) => {
    // Combine the API data and S3 data
    const combinedData = Array.isArray(tableData) ? [...tableData, ...s3Data] : [...s3Data];

    // Filter the combinedData to get entries for this specific submission version
    const relevantEntries = combinedData.filter(item =>
      item.entity_code === entityCode &&
      item.template_name === templateName &&
      item.period === submissionPeriod &&
      parseInt(item.file_version) === parseInt(submissionVersion)
    );

    // Transform the data into the timeline format
    const timelineData = relevantEntries.map(entry => ({
      status: entry.sub_status,
      timestamp: entry.log_timestamp,
      details: `Version ${entry.file_version}${entry.comments ? ` - ${entry.comments}` : ''}`,
      userName: entry.user_id
    }));

    // Sort by timestamp
    return timelineData.sort((a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  };

  // Add a useEffect to monitor data fetching
  useEffect(() => {
    if (tableData.length > 0) {
      console.log("Table data loaded:", {
        totalRows: tableData.length,
        sampleRow: tableData[0]
      });
    }
  }, [tableData]);

  useEffect(() => {
    logTimezoneInfo();
  }, []);

  return (
    <DataContext.Provider
      value={{
        tableData,
        refreshData,
        isLoading,
        dropdownOptions,
        stats,
        setStats,
        addTableData,
        getSubmissionHistory,
        calculateStats,
        templateFiles,
        templatesLoading,
        templatesError,
        refreshTemplates, // wrapper that returns Promise<void>
      }}
    >
      {children}
    </DataContext.Provider>
  );
});

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};